import { useEffect, useState } from "react";

type Point = { geohash: string; count: number; lat: number | null; lon: number | null; country?: string | null; region?: string | null };

export default function GeoHeatmap({ width = 960, height = 480 }: { width?: number; height?: number }) {
  const [points, setPoints] = useState<Point[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch("/api/geo-heat?precision=4");
        const j = await res.json();
        if (!mounted) return;
        if (j && j.ok && Array.isArray(j.data)) setPoints(j.data);
      } catch (e) {
        // ignore
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  

  // equirectangular projection: lon -> x, lat -> y
  const project = (lat: number, lon: number) => {
    const x = ((lon + 180) / 360) * width;
    const y = ((90 - lat) / 180) * height;
    return [x, y];
  };

  const maxCount = points.reduce((m, p) => Math.max(m, p.count), 0) || 1;

  return (
    <div>
      <h3 className="text-lg font-semibold mb-2" style={{ margin: 0 }}>Geo heatmap (geohash_4)</h3>
      {loading ? <div>Loading…</div> : (
        <div style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
          <svg
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="xMidYMid meet"
            style={{ width: "100%", height: "auto", background: "#eef2f7", borderRadius: 6, display: "block" }}
          >
          {/* simple graticule lines */}
          {[ -180, -120, -60, 0, 60, 120, 180 ].map((lon) => (
            <line key={`lon-${lon}`} x1={((lon + 180) / 360) * width} x2={((lon + 180) / 360) * width} y1={0} y2={height} stroke="#ddd" strokeWidth={0.5} />
          ))}
          {[ -60, -30, 0, 30, 60 ].map((lat) => (
            <line key={`lat-${lat}`} x1={0} x2={width} y1={((90 - lat) / 180) * height} y2={((90 - lat) / 180) * height} stroke="#eee" strokeWidth={0.5} />
          ))}

          {points.map((p) => {
            if (p.lat == null || p.lon == null) return null;
            const [x, y] = project(p.lat, p.lon);
            const radius = 4 + (Math.log(p.count + 1) / Math.log(maxCount + 1)) * 20;
            const alpha = 0.25 + (p.count / maxCount) * 0.75;
            return (
              <g key={p.geohash}>
                <circle cx={x} cy={y} r={radius} fill={`rgba(220,38,38,${alpha})`} stroke="rgba(220,38,38,0.8)" strokeWidth={1} />
                <text x={x + radius + 2} y={y + 4} fontSize={10} fill="#333">{p.geohash} ({p.count})</text>
              </g>
            );
          })}
          </svg>
        </div>
      )}
    </div>
  );
}
